import { useMemo, useState } from "react";
import { ArrowRight, PieChart, X } from "lucide-react";
import { computeDiet, dietGradient } from "./blindspot";

/**
 * Your Signal Diet: how the reader's attention is actually spread across the seven fields,
 * with the blindspot called out and one tap to fill it. Evidence: "less bias" and "more
 * breadth" are the top trust demands after accuracy. Renders from the long-horizon local
 * category counts; below the minimum history it shows a warm locked state instead.
 * House style: no em dashes or en dashes anywhere in copy.
 */

const DIET_COLORS: Record<string, string> = {
  "Money & Work": "#e3b969",
  "Technology & Tools": "#8fb7e8",
  "Science & Systems": "#9fd8c4",
  "Power & Policy": "#d99a9a",
  "Culture & Curiosity": "#c9a8e0",
  "Sport & Play": "#f0a562",
  "Planet & Environment": "#a8c98a",
};

export function SignalDietPanel({
  counts,
  onClose,
  onReadBlindspot,
}: {
  counts: Record<string, number>;
  onClose: () => void;
  /** Jump the feed to an unread story in the category. Returns false when none is loaded. */
  onReadBlindspot: (category: string) => boolean;
}) {
  const diet = useMemo(() => computeDiet(counts), [counts]);
  const [noneLeft, setNoneLeft] = useState(false);

  return (
    <div className="drawer-scrim center" role="dialog" aria-modal="true" aria-label="Your signal diet" onClick={onClose}>
      <section className="v3-panel diet-panel" onClick={(e) => e.stopPropagation()}>
        <header className="v3-panel-head">
          <span className="v3-eyebrow"><PieChart size={14} aria-hidden="true" /> Your signal diet</span>
          <button type="button" className="icon-button ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {!diet ? (
          <div className="diet-locked">
            <div className="diet-wheel placeholder" aria-hidden="true" />
            <h3>Ten stories unlock your diet</h3>
            <p>Read a little more and InSnip will chart where your attention actually goes, and what you never see.</p>
          </div>
        ) : (
          <>
            <div className="diet-hero">
              <div
                className="diet-wheel"
                style={{ background: dietGradient(diet, DIET_COLORS) }}
                role="img"
                aria-label={`Reading distribution across ${diet.slices.filter((s) => s.count > 0).length} fields`}
              >
                <span className="diet-total">
                  <strong>{diet.total}</strong>
                  <small>stories</small>
                </span>
              </div>
              <div className="diet-legend">
                {[...diet.slices]
                  .sort((a, b) => b.count - a.count)
                  .map((s) => (
                    <div key={s.category} className={s.count ? "diet-row" : "diet-row empty"}>
                      <span className="diet-swatch" style={{ background: DIET_COLORS[s.category] }} aria-hidden="true" />
                      <span className="diet-cat">{s.category}</span>
                      <span className="diet-n">{s.count ? `${Math.round(s.share * 100)}%` : "0"}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="diet-blindspot">
              <span className="v3-eyebrow">Your blindspot</span>
              <h3>{diet.blindspot.category}</h3>
              <p>
                {diet.blindspot.count === 0
                  ? "You have not read a single story here this season. One is enough to widen the lens."
                  : `Only ${diet.blindspot.count} of your last ${diet.total} stories. One more widens the lens.`}
              </p>
              {noneLeft ? (
                <p className="diet-none">Nothing unread in this field right now. Check back after the next drop.</p>
              ) : (
                <button
                  type="button"
                  className="ob3-cta diet-cta"
                  onClick={() => {
                    if (!onReadBlindspot(diet.blindspot.category)) setNoneLeft(true);
                  }}
                >
                  Read one from {diet.blindspot.category} <ArrowRight size={15} aria-hidden="true" />
                </button>
              )}
              {diet.lopsided ? (
                <small className="diet-note">
                  {Math.round(diet.top.share * 100)}% of your reading is {diet.top.category}. Balanced beats loud.
                </small>
              ) : null}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
